import { normalizeAgentProfile } from '../../../components/pages/settings/profile/normalize';
import type { IAgentProfile } from '../../../interface/agent-profile';
import { getAgentProfile, updateAgentPhone } from '../../../lib/agent-profile-api';
import { authType } from '../../types';
import type { IResponse } from './election';

/**
 * Hand-rolled thunks that dispatch manually rather than going through the shared
 * `httpGetMethod` helper — the same shape `otpLogin_Auth` already uses in this folder.
 *
 * Two reasons the shared helper is wrong here, and both are load-bearing while the api.core
 * rollout is outstanding: it turns a 404 into a global red toast (a 404 from ASP.NET routing
 * carries no `message`, so the helper's error extraction returns falsy and it throws), and
 * it discards the status code, so the page could not distinguish "not deployed yet" from
 * "forbidden" from "retry".
 *
 * These dispatch the NORMALIZED payload, not the response body. That is deliberate and
 * different from `getProfile_Auth`, whose stored value is the envelope — the type in
 * `interface/reducer/auth.ts` says which is which, so nothing has to remember.
 */

export const getAgentProfile_Auth =
	({ onSuccess, onFailure }: IResponse<IAgentProfile | null>) =>
	async (dispatch: any) => {
		dispatch({ type: authType.getAgentProfile_Auth.dataLoading, payload: true });

		const outcome = await getAgentProfile();

		if (!outcome.reachable) {
			// Not deployed, or the network dropped. The page falls back to what it already
			// holds; there is nothing here worth interrupting the agent about.
			dispatch({ type: authType.getAgentProfile_Auth.dataLoading, payload: false });
			onFailure?.({ httpStatus: 0 });
			return;
		}

		if (outcome.httpStatus >= 200 && outcome.httpStatus < 300) {
			const profile = normalizeAgentProfile(outcome.body);
			dispatch({ type: authType.getAgentProfile_Auth.dataAction, payload: profile });
			dispatch({ type: authType.getAgentProfile_Auth.dataLoading, payload: false });
			onSuccess?.(profile);
			return;
		}

		dispatch({ type: authType.getAgentProfile_Auth.dataLoading, payload: false });
		if (outcome.httpStatus !== 404) {
			console.warn(`[agent-profile] /User/me/profile returned ${outcome.httpStatus}`);
		}
		onFailure?.({ httpStatus: outcome.httpStatus, body: outcome.body });
	};

export const updatePhone_Auth =
	({ phoneNumber, onSuccess, onFailure }: IResponse<IAgentProfile | null> & { phoneNumber: string }) =>
	async (dispatch: any) => {
		dispatch({ type: authType.updatePhone_Auth.dataLoading, payload: true });

		const outcome = await updateAgentPhone(phoneNumber);
		dispatch({ type: authType.updatePhone_Auth.dataLoading, payload: false });

		if (!outcome.reachable) {
			onFailure?.({ httpStatus: 0, message: 'Phone updates aren’t available yet.' });
			return;
		}

		if (outcome.httpStatus >= 200 && outcome.httpStatus < 300) {
			onSuccess?.(null);
			return;
		}

		onFailure?.({
			httpStatus: outcome.httpStatus,
			message:
				outcome.httpStatus === 404 ? 'Phone updates aren’t available yet.' : outcome.message || 'That change could not be saved.',
		});
	};
